import { useState, useMemo } from 'react';
import './PhoneInput.css';

// Curated list of country dialing codes (common ones first, then rest)
export const COUNTRY_CODES = [
  { code: 'FR', dial: '+33', name: 'France', flag: '🇫🇷' },
  { code: 'BE', dial: '+32', name: 'Belgium', flag: '🇧🇪' },
  { code: 'CH', dial: '+41', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'LU', dial: '+352', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MC', dial: '+377', name: 'Monaco', flag: '🇲🇨' },
  { code: 'GB', dial: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'IE', dial: '+353', name: 'Ireland', flag: '🇮🇪' },
  { code: 'DE', dial: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: 'NL', dial: '+31', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'IT', dial: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', dial: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹' },
  { code: 'AT', dial: '+43', name: 'Austria', flag: '🇦🇹' },
  { code: 'DK', dial: '+45', name: 'Denmark', flag: '🇩🇰' },
  { code: 'SE', dial: '+46', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', dial: '+47', name: 'Norway', flag: '🇳🇴' },
  { code: 'FI', dial: '+358', name: 'Finland', flag: '🇫🇮' },
  { code: 'PL', dial: '+48', name: 'Poland', flag: '🇵🇱' },
  { code: 'CZ', dial: '+420', name: 'Czechia', flag: '🇨🇿' },
  { code: 'GR', dial: '+30', name: 'Greece', flag: '🇬🇷' },
  { code: 'US', dial: '+1', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: 'JP', dial: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: 'CN', dial: '+86', name: 'China', flag: '🇨🇳' },
  { code: 'HK', dial: '+852', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'SG', dial: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: 'KR', dial: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: 'AE', dial: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'IL', dial: '+972', name: 'Israel', flag: '🇮🇱' },
  { code: 'AU', dial: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', dial: '+64', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'MA', dial: '+212', name: 'Morocco', flag: '🇲🇦' },
  { code: 'TN', dial: '+216', name: 'Tunisia', flag: '🇹🇳' },
  { code: 'DZ', dial: '+213', name: 'Algeria', flag: '🇩🇿' },
];

const DEFAULT_DIAL = '+33';

// Split a full phone string (e.g. "+33612345678") into { dial, local }
function splitPhone(raw) {
  if (!raw) return { dial: DEFAULT_DIAL, local: '' };
  const trimmed = raw.trim();
  // Try to match the longest dial prefix from the list
  const sorted = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (trimmed.startsWith(c.dial)) {
      return { dial: c.dial, local: trimmed.slice(c.dial.length).trim() };
    }
  }
  // No match — treat entire value as local
  return { dial: DEFAULT_DIAL, local: trimmed.replace(/^\+/, '') };
}

export default function PhoneInput({ value, onChange, required, className = '' }) {
  const { dial: initialDial, local: initialLocal } = useMemo(() => splitPhone(value), [value]);
  const [dial, setDial] = useState(initialDial);
  const [local, setLocal] = useState(initialLocal);

  const buildFull = (d, l) => {
    const cleaned = (l || '').replace(/[^\d\s]/g, '').trim();
    return cleaned ? `${d} ${cleaned}`.trim() : '';
  };

  const handleDialChange = (newDial) => {
    setDial(newDial);
    onChange(buildFull(newDial, local));
  };

  const handleLocalChange = (newLocal) => {
    setLocal(newLocal);
    onChange(buildFull(dial, newLocal));
  };

  return (
    <div className={`phone-input ${className}`}>
      <select
        className="phone-dial"
        value={dial}
        onChange={e => handleDialChange(e.target.value)}
        aria-label="Country dialing code"
      >
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.dial}>
            {c.flag} {c.dial} {c.name}
          </option>
        ))}
      </select>
      <input
        className="input phone-local"
        type="tel"
        value={local}
        onChange={e => handleLocalChange(e.target.value)}
        placeholder="6 12 34 56 78"
        required={required}
      />
    </div>
  );
}
